import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  motion,
  MotionValue,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { Container, text } from '../styles/GlobalStyles';
import Device from './simulator/Device';
import StatusBar from './simulator/StatusBar';
import DynamicIsland from './simulator/DynamicIsland';
import { ArchitectureLayer, DataFlowLayer, InterfaceLayer, NetworkLayer } from './craft/Layers';

// Scroll map (0 → 1 across the pinned section):
//   0.06 – 0.70  four steps; each highlights its layer while the gaps between layers close
//   0.70 – 0.86  the stack rotates from isometric to flat, lining up with the phone's screen
//   0.84 – 0.92  the real phone fades in over the flattened stack
const STEP_START = 0.06;
const STEP_SPAN = 0.16;
const STEPS = 4;

const Section = styled.section<{ $pinned: boolean }>`
  position: relative;
  height: ${props => (props.$pinned ? '420vh' : 'auto')};
`;

const Sticky = styled.div<{ $pinned: boolean }>`
  position: ${props => (props.$pinned ? 'sticky' : 'relative')};
  top: 0;
  height: ${props => (props.$pinned ? '100svh' : 'auto')};
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: ${props => (props.$pinned ? '60px 0 0' : '128px 0')};
`;

const Layout = styled(Container)`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  align-items: center;
  gap: 48px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
    align-content: center;
    height: 100%;
  }
`;

const Copy = styled.div`
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    text-align: left;
  }
`;

const Title = styled.h2`
  font-size: ${text.xxl};
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: ${props => props.theme.colors.text};
  text-wrap: balance;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  margin-top: 14px;
  max-width: 40ch;
  color: ${props => props.theme.colors.textSecondary};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${text.sm};
    margin-top: 8px;
  }
`;

const Steps = styled.ol`
  list-style: none;
  margin-top: 48px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-top: 18px;
  }
`;

const Step = styled.li<{ $state: 'active' | 'done' | 'next' }>`
  position: relative;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  padding: 16px 0;
  border-top: 1px solid ${props => props.theme.colors.border};
  opacity: ${props => (props.$state === 'active' ? 1 : props.$state === 'done' ? 0.55 : 0.3)};
  transition: opacity 0.3s ease;

  span {
    font-size: ${text.xs};
    font-variant-numeric: tabular-nums;
    color: ${props => props.theme.colors.textTertiary};
    padding-top: 3px;
  }

  strong {
    display: block;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
  }

  p {
    margin-top: 4px;
    font-size: ${text.sm};
    line-height: 1.5;
    color: ${props => props.theme.colors.textSecondary};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => (props.$state === 'active' ? 'grid' : 'none')};
    border-top: none;
    padding: 0;
  }
`;

const StepBar = styled(motion.i)`
  position: absolute;
  top: -1px;
  left: 0;
  height: 1px;
  width: 100%;
  background: ${props => props.theme.colors.primary};
  transform-origin: left;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Stage = styled.div`
  --phone-w: clamp(220px, 20vw, 280px);
  position: relative;
  display: grid;
  place-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    --phone-w: min(196px, 50vw);
  }
`;

const Anchor = styled.div`
  position: relative;
`;

const Stack = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  perspective: 2400px;
  pointer-events: none;
`;

const Plane = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
`;

const Layer = styled(motion.div)<{ $radius: number; $active: boolean; $hidden: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: ${props => props.$radius}px;
  overflow: hidden;
  container-type: inline-size;
  box-shadow:
    0 0 0 1px ${props => (props.$active ? props.theme.colors.primary : props.theme.colors.borderStrong)},
    0 30px 60px -30px rgba(0, 0, 0, ${props => (props.$hidden ? 0 : 0.35)});
  transition: box-shadow 0.35s ease;
  backface-visibility: hidden;
`;

type LayerState = 'active' | 'above' | 'below' | 'merged';

// Layers above the active one are peeled back so the active layer's content is visible.
const Dim = styled.div<{ $state: LayerState }>`
  position: absolute;
  inset: 0;
  opacity: ${props => ({ active: 1, above: 0.06, below: 0.6, merged: 1 }[props.$state])};
  transition: opacity 0.35s ease;
`;

const PhoneLayer = styled(motion.div)`
  position: relative;
  z-index: 1;
`;

const layers = [InterfaceLayer, ArchitectureLayer, DataFlowLayer, NetworkLayer];

const StackLayer: React.FC<{
  index: number;
  separation: MotionValue<number>;
  gap: number;
  radius: number;
  state: LayerState;
  fade: MotionValue<number>;
}> = ({ index, separation, gap, radius, state, fade }) => {
  // Layer 0 (the interface) sits on top; deeper layers sit further down the z axis.
  const z = useTransform(separation, value => (STEPS - 1 - index) * gap * value);
  const Content = layers[index];
  return (
    <Layer $radius={radius} $active={state === 'active'} $hidden={state === 'above'} style={{ z, opacity: fade }}>
      <Dim $state={state}>
        <Content />
      </Dim>
    </Layer>
  );
};

const StepProgress: React.FC<{ progress: MotionValue<number>; index: number }> = ({ progress, index }) => {
  const start = STEP_START + index * STEP_SPAN;
  const scaleX = useTransform(progress, [start, start + STEP_SPAN], [0, 1]);
  return <StepBar style={{ scaleX }} />;
};

const Craft: React.FC = () => {
  const { content, effectiveTheme } = useAppContext();
  const { craft } = content;
  const reduceMotion = useReducedMotion();
  const pinned = !reduceMotion;

  const sectionRef = useRef<HTMLElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const [screen, setScreen] = useState({ w: 0, h: 0 });
  const [step, setStep] = useState(0);
  const [merged, setMerged] = useState(false);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });
  const smooth = useSpring(scrollYProgress, { stiffness: 200, damping: 40, restDelta: 0.0005 });
  const settled = useMotionValue(1);
  const progress = pinned ? smooth : settled;

  // Measure the phone's screen so the flattened stack lands exactly on it.
  useLayoutEffect(() => {
    const element = screenRef.current;
    if (!element) return;
    const measure = () => setScreen({ w: element.offsetWidth, h: element.offsetHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useMotionValueEvent(progress, 'change', value => {
    const next = Math.min(STEPS - 1, Math.max(0, Math.floor((value - STEP_START) / STEP_SPAN)));
    setStep(current => (current === next ? current : next));
    setMerged(value > 0.7);
  });

  const separation = useTransform(progress, [STEP_START, STEP_START + STEP_SPAN * STEPS], [1, 0]);
  const flatten = useTransform(progress, [0.7, 0.86], [0, 1]);
  const rotateX = useTransform(flatten, [0, 1], [56, 0]);
  const rotateZ = useTransform(flatten, [0, 1], [-34, 0]);
  const lift = useTransform(separation, value => value * screen.w * 0.42);
  const stackFade = useTransform(progress, [0.86, 0.92], [1, 0]);
  const phoneOpacity = useTransform(progress, [0.84, 0.92], [0, 1]);

  // Device screen radius is 0.132 of the frame width; the screen is 0.924 of the frame.
  const radius = (screen.w / 0.924) * 0.132;
  const gap = screen.w * 0.4;

  return (
    <Section ref={sectionRef} id="craft" $pinned={pinned} aria-label={craft.title}>
      <Sticky $pinned={pinned}>
        <Layout>
          <Copy>
            <Title>{craft.title}</Title>
            <Subtitle>{craft.subtitle}</Subtitle>
            <Steps>
              {craft.steps.map((item, index) => (
                <Step key={item.title} $state={!pinned || index === step ? 'active' : index < step ? 'done' : 'next'}>
                  {pinned && <StepProgress progress={progress} index={index} />}
                  <span>0{index + 1}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.body}</p>
                  </div>
                </Step>
              ))}
            </Steps>
          </Copy>

          <Stage>
            <Anchor>
              <PhoneLayer style={{ opacity: phoneOpacity }}>
                <Device width="var(--phone-w)" screenRef={screenRef}>
                  <InterfaceLayer />
                  <StatusBar tone={effectiveTheme === 'dark' ? 'light' : 'dark'} />
                  <DynamicIsland state="idle" />
                </Device>
              </PhoneLayer>

              {pinned && screen.w > 0 && (
                <Stack style={{ width: screen.w, height: screen.h, marginLeft: -screen.w / 2, marginTop: -screen.h / 2 }} aria-hidden="true">
                  <Plane style={{ rotateX, rotateZ, y: lift }}>
                    {[3, 2, 1, 0].map(index => (
                      <StackLayer
                        key={index}
                        index={index}
                        separation={separation}
                        gap={gap}
                        radius={radius}
                        state={merged ? 'merged' : index === step ? 'active' : index < step ? 'above' : 'below'}
                        fade={stackFade}
                      />
                    ))}
                  </Plane>
                </Stack>
              )}
            </Anchor>
          </Stage>
        </Layout>
      </Sticky>
    </Section>
  );
};

export default Craft;
